// SCR-12 recording policy (plan 8.4.12, 11.3, 11.6, 11.9, 7.5): company cap, notice versions (10 sections),
// redaction rules, retention values, content-view control values, deletion requests, legal holds, retention job.
// Saving is optimistic-locked by policy version; each changed value becomes one `policy.change` audit row.
import { z } from 'zod';
import { ConsentItem, DeletionScope, DeletionStatus, JobStatus, ReasonCategory, RecordLevel, RedactionKind, RedactionMethod } from '../enums.js';
import { AccessControlValues, RetentionTarget } from '../controls.js';
import { Count, EpochMs, Id, LocalizedText, PageMeta, PageQuery, PersonRef, RecordRef, TeamRef, UserId, Ymd } from '../common.js';
import { AuditEntry } from './admin-audit.js';

export const RetentionRow = z.object({
  target: RetentionTarget,
  days: z.int().positive().nullable(),
  min: z.int().positive().nullable(),
  max: z.int().positive().nullable(),
  /** Choices offered on screen (values outside min–max are disabled). */
  options: z.array(z.int().positive()),
  editable: z.boolean(),
});

export const REDACTION_RULE_IDS = ['secret', 'assign', 'rrn', 'contact', 'finance', 'blob', 'paths'] as const;
export const RedactionRuleId = z.enum(REDACTION_RULE_IDS);
export const RedactionRule = z.object({
  id: RedactionRuleId,
  method: RedactionMethod,
  /** Fixed rules cannot change method; contact/finance choose token or marker. */
  fixed: z.boolean(),
  options: z.array(RedactionMethod),
  fields: z.array(z.enum(['text', 'paths'])),
});

export const NOTICE_AUTO_SOURCES = ['caps', 'retention', 'roles', 'redaction', 'integrations'] as const;
/** Sections 2/4/5/7/8 are filled from settings; 1/3/6/9/10 are edited by notice.manage (plan 11.3.5). */
export const NOTICE_AUTO_SECTIONS: Record<number, (typeof NOTICE_AUTO_SOURCES)[number]> = { 2: 'caps', 4: 'retention', 5: 'roles', 7: 'redaction', 8: 'integrations' };
export const NOTICE_EDITABLE_SECTIONS = [1, 3, 6, 9, 10] as const;

/** GET /api/v1/admin/policy — current policy + facts the screen shows next to it. */
export const PolicyView = z.object({
  version: z.int().positive(),
  updatedAt: EpochMs,
  updatedBy: PersonRef.nullable(),
  caps: z.record(ConsentItem, z.boolean()),
  /** Derived ceiling level; null = custom (non-cumulative) cap. */
  preset: RecordLevel.nullable(),
  maxLevel: RecordLevel,
  defaultLevel: RecordLevel,
  /** server_upload is only meaningful while the PC helper (C2, P6) is on. */
  serverUploadAvailable: z.boolean(),
  retention: z.array(RetentionRow),
  access: AccessControlValues,
  /** Fixed at 3 (raised only by environment). */
  smallGroupMin: z.int().min(1),
  smallTeams: z.array(z.object({ team: TeamRef, members: Count })),
  allowValueSort: z.boolean(),
  redaction: z.object({ rules: z.array(RedactionRule), secretPathRules: z.array(z.string()) }),
  consentDistribution: z.object({ byLevel: z.record(z.enum(['1', '2', '3', '4']), Count), total: Count, aboveCap: Count }),
  reconsentWaiting: Count,
  notice: z.object({ current: z.string(), outOfSync: z.boolean(), pendingSections: z.array(z.int().min(1).max(10)) }),
  pendingDeletions: Count,
  /** Dotted setting paths locked by environment. Values and secrets are never returned here. */
  locks: z.array(z.object({ path: z.string(), source: z.literal('environment'), env: z.string().regex(/^[A-Z][A-Z0-9_]*$/) })),
});
export type PolicyView = z.infer<typeof PolicyView>;

export const PolicyPatch = z.object({
  caps: z.partialRecord(ConsentItem, z.boolean()).optional(),
  retention: z.partialRecord(RetentionTarget, z.int().positive()).optional(),
  access: AccessControlValues.partial().optional(),
  allowValueSort: z.boolean().optional(),
  redactionMethods: z.object({ contact: z.enum(['token', 'marker']).optional(), finance: z.enum(['token', 'marker']).optional() }).optional(),
});
/** PUT /api/v1/admin/policy — 409 stale_version when `expectedVersion` is not current. */
export const PolicySaveRequest = z.object({ expectedVersion: z.int().positive(), changes: PolicyPatch, reason: z.string().trim().max(200).optional() });
export const PolicySaveResponse = z.object({
  policy: PolicyView,
  changes: z.array(z.object({ path: z.string(), from: z.unknown(), to: z.unknown(), noticeSection: z.int().min(1).max(10).nullable() })),
  noticeDraftCreated: z.boolean(),
});
export const PolicyHistoryResponse = z.object({ items: z.array(AuditEntry) });

/** Notice versions and the working draft (notice.manage edits, publishing asks everyone to re-consent). */
export const NoticeVersionRow = z.object({
  version: z.string(),
  publishedAt: EpochMs,
  publishedBy: PersonRef.nullable(),
  hash: z.string(),
  changes: LocalizedText.nullable(),
  trigger: z.array(z.enum([...NOTICE_AUTO_SOURCES, 'manual'])),
});
export const NoticeDraftSection = z.object({
  n: z.int().min(1).max(10),
  auto: z.enum(NOTICE_AUTO_SOURCES).nullable(),
  /** Markdown-lite (paragraphs, `- ` lists, **bold**); never HTML. */
  body: LocalizedText,
  changed: z.boolean(),
  edited: z.boolean(),
});
export const NoticeAdminResponse = z.object({
  versions: z.array(NoticeVersionRow),
  draft: z.object({ nextVersion: z.string(), pendingSections: z.array(z.int().min(1).max(10)), sections: z.array(NoticeDraftSection).length(10), draftHash: z.string() }),
  reconsentWaiting: Count,
});
export const NoticeSectionParams = z.object({ n: z.coerce.number().int().refine((n) => (NOTICE_EDITABLE_SECTIONS as readonly number[]).includes(n), 'only sections 1, 3, 6, 9, 10 are editable') });
/** `text: null` drops the draft edit (back to the published text). 1–1200 chars (blank is refused), no HTML. */
export const NoticeSectionEditRequest = z.object({
  lang: z.enum(['ko', 'en']),
  text: z
    .string()
    .trim()
    .min(1)
    .max(1200)
    .refine((s) => !/<\/?[a-zA-Z!][^>]*>/.test(s), 'HTML tags are not allowed')
    .nullable(),
});
export const NoticePublishRequest = z.object({ draftHash: z.string() });

/** POST /api/v1/admin/redaction/preview — server-side masking preview with the draft methods (redact package). */
export const RedactionPreviewRequest = z.object({ text: z.string().max(600), methods: PolicyPatch.shape.redactionMethods });
export const RedactionPreviewResponse = z.object({ masked: z.string(), count: Count, byKind: z.partialRecord(RedactionKind, Count) });

/** Deletion requests for processing (deletion.process). Hold/reject need a memo ≥ 5 chars (shown to the person). */
export const DeletionRequestAdminRow = z.object({
  id: Id,
  /** Display code ("DR-2088"). */
  code: z.string().regex(/^DR-\d{4,}$/),
  person: PersonRef,
  requestedAt: EpochMs,
  scope: DeletionScope,
  record: RecordRef.nullable(),
  from: Ymd.nullable(),
  to: Ymd.nullable(),
  count: Count,
  auto: z.boolean(),
  status: DeletionStatus,
  dueAt: EpochMs.nullable(),
  handledBy: PersonRef.nullable(),
  handledAt: EpochMs.nullable(),
  memo: z.string().nullable(),
  /** The person is under legal hold → delete is blocked (hold instead). */
  holdActive: z.boolean(),
});
export const DeletionAdminListQuery = z.object({ status: z.enum(['blocked', 'deleted', 'held', 'rejected', 'all']).default('all') });
export const DeletionAdminListResponse = z.object({ items: z.array(DeletionRequestAdminRow), pending: Count });
export const DeletionProcessRequest = z
  .object({ action: z.enum(['delete', 'hold', 'reject']), memo: z.string().trim().max(200).optional() })
  .superRefine((b, ctx) => {
    if (b.action !== 'delete' && (!b.memo || b.memo.length < 5)) ctx.addIssue({ code: 'custom', message: 'memo ≥ 5 chars for hold/reject', path: ['memo'] });
  });

/** Legal holds (legal_hold.manage; plan 11.9.3) — their own entity, auto-expire at `until`. */
export const LegalHoldRow = z.object({
  id: Id,
  person: PersonRef,
  until: Ymd.nullable(),
  reasonCategory: ReasonCategory,
  reason: z.string(),
  setBy: PersonRef,
  setAt: EpochMs,
  releasedBy: PersonRef.nullable(),
  releasedAt: EpochMs.nullable(),
  releaseReason: z.string().nullable(),
  notifyDeferredUntil: EpochMs.nullable(),
});
export const LegalHoldListResponse = z.object({ items: z.array(LegalHoldRow), active: Count });
export const LegalHoldCreateRequest = z.object({ userId: UserId, until: Ymd, reasonCategory: ReasonCategory, reason: z.string().trim().min(5).max(200) });
export const LegalHoldReleaseRequest = z.object({ reason: z.string().trim().min(5).max(200) });
/** A separate two-person approval, bounded by the hold's expiry (plan 11.9.3). */
export const LegalHoldNotifyDeferralRequest = z.object({ until: Ymd, reason: z.string().trim().min(10).max(200) });
export const LegalHoldCandidatesQuery = z.object({ q: z.string().trim().max(60).optional(), ...PageQuery.shape });
export const LegalHoldCandidatesResponse = z.object({ items: z.array(z.object({ person: PersonRef, team: TeamRef.nullable() })), page: PageMeta });
export const HoldParams = z.object({ holdId: Id });

/** Retention cleanup runs (retention.manage; daily 03:30, "지금 실행"). */
export const RetentionJobRow = z.object({
  id: Id,
  startedAt: EpochMs,
  finishedAt: EpochMs.nullable(),
  status: JobStatus,
  trigger: z.enum(['schedule', 'manual']),
  actor: PersonRef.nullable(),
  deleted: z.record(z.string(), Count),
});
export const RetentionJobListResponse = z.object({ items: z.array(RetentionJobRow) });

export const adminPolicySchemas = {
  LegalHoldCandidatesQuery,
  LegalHoldCandidatesResponse,
  LegalHoldNotifyDeferralRequest,
  PolicyView,
  PolicyPatch,
  PolicySaveRequest,
  PolicySaveResponse,
  PolicyHistoryResponse,
  NoticeAdminResponse,
  NoticeSectionParams,
  NoticeSectionEditRequest,
  NoticePublishRequest,
  NoticeVersionRow,
  RedactionPreviewRequest,
  RedactionPreviewResponse,
  DeletionAdminListQuery,
  DeletionAdminListResponse,
  DeletionProcessRequest,
  DeletionRequestAdminRow,
  LegalHoldListResponse,
  LegalHoldCreateRequest,
  LegalHoldReleaseRequest,
  LegalHoldRow,
  HoldParams,
  RetentionJobListResponse,
} as const;
